import { LicenseType, type SeatChangeRequest, SeatChangeType, type SeatInfoDTO } from "@pentacode/core";
import { defineTransitions } from "../../lib/transitions";

export type TierState = "unassigned" | "assigned" | "expiring" | "scheduled";

export interface SeatSnapshot {
    lite: TierState;
    pro: TierState;
}

export interface SeatCount {
    current: number;
    futureCurrent: number;
    futureNext: number;
}

// User-driven actions only. `docs/projects/license-seat-system/seat-actions-and-states.md`
// also documents `expired(version)` transitions for the period boundary, but
// those are backend territory — the booking UI never fires them. A seat that
// has actually expired appears as `unassigned` once the DTO refetches (see
// `tierFromValidity`).
export type SeatEvent =
    | { readonly type: "ASSIGN_LITE" }
    | { readonly type: "ASSIGN_PRO" }
    | { readonly type: "UNASSIGN_LITE" }
    | { readonly type: "UNASSIGN_PRO" };

export const applyEvent = defineTransitions<SeatSnapshot, SeatEvent>({
    ASSIGN_LITE: [
        // Downgrade — Pro is the currently-paid tier, schedule Lite for next period.
        {
            when: (s) => s.pro === "assigned" || s.pro === "expiring",
            to: (s) => ({ ...s, lite: "scheduled", pro: "expiring" }),
        },
        // Uncancel an expiring Lite.
        { when: (s) => s.lite === "expiring", to: (s) => ({ ...s, lite: "assigned" }) },
        // Plain assign from unassigned.
        { when: (s) => s.lite === "unassigned", to: (s) => ({ ...s, lite: "assigned" }) },
        // Fall-through: already-assigned / already-scheduled — no-op.
    ],
    ASSIGN_PRO: [
        // Upgrade — Lite is currently committed.
        {
            when: (s) => s.lite === "assigned" || s.lite === "expiring",
            to: () => ({ lite: "unassigned", pro: "assigned" }),
        },
        // Re-upgrade after downgrade — pro=expiring + lite=scheduled.
        {
            when: (s) => s.lite === "scheduled" && s.pro === "expiring",
            to: () => ({ lite: "unassigned", pro: "assigned" }),
        },
        // Uncancel expiring Pro.
        { when: (s) => s.pro === "expiring", to: (s) => ({ ...s, pro: "assigned" }) },
        // Plain assign from unassigned.
        { when: (s) => s.pro === "unassigned", to: (s) => ({ ...s, pro: "assigned" }) },
        // Fall-through: already-assigned — no-op.
    ],
    UNASSIGN_LITE: [
        // Cancel-after-downgrade — drop the scheduled Lite, keep Pro expiring.
        { when: (s) => s.lite === "scheduled", to: (s) => ({ ...s, lite: "unassigned" }) },
        // Cancel a committed Lite — runs through period end.
        { when: (s) => s.lite === "assigned", to: (s) => ({ ...s, lite: "expiring" }) },
        // Fall-through: Lite not active — no-op.
    ],
    UNASSIGN_PRO: [
        { when: (s) => s.pro === "assigned", to: (s) => ({ ...s, pro: "expiring" }) },
        // Fall-through: Pro not assigned — no-op.
    ],
});

export const SEAT_INITIAL_STATE: SeatSnapshot = { lite: "unassigned", pro: "unassigned" };

export function tierFromValidity(seat: { validFrom: Date | null; validUntil: Date | null } | undefined): TierState {
    if (!seat) return "unassigned";
    if (seat.validFrom && seat.validFrom.getTime() > Date.now()) return "scheduled";
    if (!seat.validUntil) return "assigned";
    return seat.validUntil.getTime() > Date.now() ? "expiring" : "unassigned";
}

// Editing-mode semantics. Each click is the user's intent for one cell, read
// from what that cell is currently showing (effective state). Translate to an
// event, then apply it to the BASELINE — cross-tier rules in the reducer then
// see the committed state and fire correctly. The other tier's pending change
// is dropped because the reducer starts from baseline.
function clickIntent(tier: TierState): "assign" | "unassign" {
    switch (tier) {
        case "unassigned":
        case "expiring":
            return "assign"; // empty / clock → fill
        case "assigned":
        case "scheduled":
            return "unassign"; // check / calendar → unfill
    }
}

export function nextEffective(baseline: SeatSnapshot, current: SeatSnapshot, licenseType: LicenseType): SeatSnapshot {
    const isLite = licenseType === LicenseType.aiAssistantLite;
    const intent = clickIntent(isLite ? current.lite : current.pro);
    const eventType = (
        intent === "assign" ? (isLite ? "ASSIGN_LITE" : "ASSIGN_PRO") : isLite ? "UNASSIGN_LITE" : "UNASSIGN_PRO"
    ) satisfies SeatEvent["type"];
    return applyEvent(baseline, { type: eventType });
}

// Backend contract: at most one `SeatChangeRequest` per employee per batch.
// Cross-tier flows (upgrade / downgrade / re-upgrade / cancel-after-downgrade)
// happen via server-side side-effects on a single operative request — sending
// two changes for one employee trips the backend's dedup check. The mapping
// below is the inverse of `nextEffective`: for each reachable
// `(baseline → effective)` transition, the single change the backend expects.
type SnapshotKey = `${TierState}_${TierState}`;

// Reachable transitions per `docs/projects/license-seat-system/seat-actions-and-states.md`
// (UI Booking Machine). Used to force the mapping table to cover every
// reducer-emitted transition; reducer drift surfaces at compile time when a
// new transition is added, or at runtime via the lookup guard below.
type ReachableTransition =
    | "unassigned_unassigned->assigned_unassigned"
    | "unassigned_unassigned->unassigned_assigned"
    | "assigned_unassigned->expiring_unassigned"
    | "assigned_unassigned->unassigned_assigned"
    | "unassigned_assigned->unassigned_expiring"
    | "unassigned_assigned->scheduled_expiring"
    | "expiring_unassigned->assigned_unassigned"
    | "expiring_unassigned->unassigned_assigned"
    | "unassigned_expiring->unassigned_assigned"
    | "unassigned_expiring->scheduled_expiring"
    | "scheduled_expiring->unassigned_assigned"
    | "scheduled_expiring->unassigned_expiring";

const snapshotKey = (s: SeatSnapshot): SnapshotKey => `${s.lite}_${s.pro}`;

const TRANSITION_TO_CHANGE: Record<ReachableTransition, Omit<SeatChangeRequest, "employeeId">> = {
    // Single-tier
    "unassigned_unassigned->assigned_unassigned": {
        licenseType: LicenseType.aiAssistantLite,
        type: SeatChangeType.AssignSeat,
    },
    "unassigned_unassigned->unassigned_assigned": {
        licenseType: LicenseType.aiAssistantPro,
        type: SeatChangeType.AssignSeat,
    },
    "assigned_unassigned->expiring_unassigned": {
        licenseType: LicenseType.aiAssistantLite,
        type: SeatChangeType.CancelSeat,
    },
    "unassigned_assigned->unassigned_expiring": {
        licenseType: LicenseType.aiAssistantPro,
        type: SeatChangeType.CancelSeat,
    },
    "expiring_unassigned->assigned_unassigned": {
        licenseType: LicenseType.aiAssistantLite,
        type: SeatChangeType.AssignSeat,
    },
    "unassigned_expiring->unassigned_assigned": {
        licenseType: LicenseType.aiAssistantPro,
        type: SeatChangeType.AssignSeat,
    },
    // Cross-tier (collapsed to the single operative request)
    "assigned_unassigned->unassigned_assigned": {
        licenseType: LicenseType.aiAssistantPro,
        type: SeatChangeType.AssignSeat,
    }, // upgrade
    "expiring_unassigned->unassigned_assigned": {
        licenseType: LicenseType.aiAssistantPro,
        type: SeatChangeType.AssignSeat,
    }, // upgrade-from-expiring-lite
    "unassigned_assigned->scheduled_expiring": {
        licenseType: LicenseType.aiAssistantLite,
        type: SeatChangeType.AssignSeat,
    }, // downgrade
    "unassigned_expiring->scheduled_expiring": {
        licenseType: LicenseType.aiAssistantLite,
        type: SeatChangeType.AssignSeat,
    }, // downgrade-from-expiring-pro
    "scheduled_expiring->unassigned_assigned": {
        licenseType: LicenseType.aiAssistantPro,
        type: SeatChangeType.AssignSeat,
    }, // re-upgrade — drops scheduled lite as side-effect
    "scheduled_expiring->unassigned_expiring": {
        licenseType: LicenseType.aiAssistantPro,
        type: SeatChangeType.CancelSeat,
    }, // cancel-after-downgrade — drops scheduled lite as side-effect
};

export function diffSnapshot(employeeId: number, before: SeatSnapshot, after: SeatSnapshot): SeatChangeRequest[] {
    if (before.lite === after.lite && before.pro === after.pro) return [];
    const key = `${snapshotKey(before)}->${snapshotKey(after)}`;
    if (!(key in TRANSITION_TO_CHANGE)) {
        throw new Error(`seat-machine: no SeatChangeRequest mapping for transition ${key}`);
    }
    const change = TRANSITION_TO_CHANGE[key as ReachableTransition];
    return [{ ...change, employeeId }];
}

const isCurrentTier = (tier: TierState) => tier === "assigned" || tier === "expiring";
const isNextTier = (tier: TierState) => tier === "assigned" || tier === "scheduled";

function tierOf(snapshot: SeatSnapshot, licenseType: LicenseType): TierState {
    return licenseType === LicenseType.aiAssistantLite ? snapshot.lite : snapshot.pro;
}

// Booked-seat counts per license type, combining the committed DTO state with
// the user's pending (unsaved) changes in `effectiveSnapshots`. The counts must
// range over the union of employees with an existing seat AND employees with a
// pending change: counting only DTO-seat employees drops freshly-assigned
// employees who have no seat record yet, undercounting booked seats.
export function computeSeatCounts(
    seats: SeatInfoDTO[],
    effectiveSnapshots: Map<number, SeatSnapshot>
): Record<LicenseType, SeatCount> {
    const findSeat = (employeeId: number, licenseType: LicenseType) =>
        seats.find((s) => s.employeeId === employeeId && s.licenseType === licenseType);
    const dtoSnapshot = (employeeId: number): SeatSnapshot => ({
        lite: tierFromValidity(findSeat(employeeId, LicenseType.aiAssistantLite)),
        pro: tierFromValidity(findSeat(employeeId, LicenseType.aiAssistantPro)),
    });
    const effectiveSnapshot = (employeeId: number): SeatSnapshot =>
        effectiveSnapshots.get(employeeId) ?? dtoSnapshot(employeeId);

    const employeeIds = [...new Set([...seats.map((s) => s.employeeId), ...effectiveSnapshots.keys()])];

    const countsFor = (licenseType: LicenseType): SeatCount => ({
        current: employeeIds.filter((id) => isCurrentTier(tierOf(dtoSnapshot(id), licenseType))).length,
        futureCurrent: employeeIds.filter((id) => isCurrentTier(tierOf(effectiveSnapshot(id), licenseType))).length,
        futureNext: employeeIds.filter((id) => isNextTier(tierOf(effectiveSnapshot(id), licenseType))).length,
    });

    return {
        [LicenseType.aiAssistantLite]: countsFor(LicenseType.aiAssistantLite),
        [LicenseType.aiAssistantPro]: countsFor(LicenseType.aiAssistantPro),
    };
}
